/**
 * Restricted shipping states — LEGAL, NOT A PREFERENCE.
 *
 * GLP-1 medication cannot be sold into Louisiana (LA) or Mississippi (MS).
 * State law prohibits it.
 *
 * Rules for anyone touching this file:
 *   - NEVER add LA or MS back to a state dropdown, autocomplete or allow-list.
 *   - A list that merely omits them is NOT a gate when the field is free text
 *     with suggestions. Free-text state fields must call isRestrictedState().
 *   - Every state field needs a gate, including the post-payment shipping
 *     address screens in the dashboard.
 *   - Never remove or weaken this list. If a state list looks "incomplete"
 *     because these two are missing, that is deliberate.
 */

// Two-letter abbreviations we cannot ship to.
export const RESTRICTED_STATES = ["LA", "MS"];

// Full names, so callers can build a human-readable message.
export const RESTRICTED_STATE_NAMES = {
  LA: "Louisiana",
  MS: "Mississippi",
};

// Wording already used in production. Reuse it, don't invent new copy.
export const RESTRICTED_STATE_MESSAGE = "Service is not available in this state";

/**
 * True when the supplied value is Louisiana or Mississippi.
 * Accepts either the abbreviation ("la", "MS") or the full name
 * ("louisiana", "Mississippi"), with any casing or surrounding whitespace.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isRestrictedState(value) {
  if (!value || typeof value !== "string") return false;

  const normalized = value.trim().toUpperCase();
  if (!normalized) return false;

  if (RESTRICTED_STATES.includes(normalized)) return true;

  return Object.values(RESTRICTED_STATE_NAMES).some(
    (name) => name.toUpperCase() === normalized
  );
}

/**
 * Removes any restricted state from a list before it is shown to a customer.
 * Handles the three list shapes used across this app:
 *   - plain abbreviations:   ["AL", "AK", ...]
 *   - plain full names:      ["Alabama", "Alaska", ...]
 *   - objects:               [{ name, abbr }], [{ value, label }], ...
 *
 * @param {Array} list
 * @param {(item: any) => string} [accessor] pulls the state out of an item
 * @returns {Array}
 */
export function withoutRestrictedStates(list, accessor) {
  if (!Array.isArray(list)) return list;

  return list.filter((item) => {
    if (typeof accessor === "function") return !isRestrictedState(accessor(item));
    if (typeof item === "string") return !isRestrictedState(item);
    if (item && typeof item === "object") {
      return !(
        isRestrictedState(item.abbr) ||
        isRestrictedState(item.abbrev) ||
        isRestrictedState(item.value) ||
        isRestrictedState(item.name) ||
        isRestrictedState(item.label)
      );
    }
    return true;
  });
}

export default {
  RESTRICTED_STATES,
  RESTRICTED_STATE_NAMES,
  RESTRICTED_STATE_MESSAGE,
  isRestrictedState,
  withoutRestrictedStates,
};
